// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A visually engaging loading component for asset rollforward reports.
 *
 * Displays a checklist of analysis steps that get "checked off" sequentially
 * while waiting for the API response. Provides users with feedback that
 * analysis work is happening rather than showing a simple spinner.
 *
 * Timing is dynamically calculated based on the organization's asset count.
 * Separate configurations for inventory and fixed-assets tabs with
 * appropriate step labels for each type.
 *
 * Steps complete at timed intervals proportional to expected load time,
 * and accelerate to completion once actual data arrives.
 */
import {
  ReportLoaderDisplay,
  type RollforwardReportType,
  rollforwardReportConfigs,
  useReportLoaderProgress,
} from '@/report/components/report-loader';

interface Props {
  /** When true, accelerates remaining steps and completes quickly */
  isDataReady?: boolean;
  /** Called when all steps are complete */
  onComplete?: () => void;
  /** Type of rollforward report being loaded (affects step labels and timing) */
  reportType: RollforwardReportType;
}

/**
 * Displays a pseudo-progress loader with a checklist of analysis steps.
 * Fetches asset count to calculate realistic step timing, then animates
 * through steps proportionally. Accelerates when actual data arrives.
 */
export function RollforwardReportLoader(props: Props) {
  const { isDataReady, onComplete, reportType } = props;

  const config = rollforwardReportConfigs[reportType];

  // Rollforward reports don't scale with date range - they compare two points
  // in time, so we use asset count only (not asset-days)
  const progress = useReportLoaderProgress({
    config,
    isDataReady,
    onComplete,
  });

  return (
    <ReportLoaderDisplay
      currentStep={progress.currentStep}
      completedSteps={progress.completedSteps}
      stepLabels={config.stepLabels}
    />
  );
}
